import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { Manager } from "socket.io-client";

import { APP_LOCAL_KEYS } from "@/app/constants/constants";
import { env } from "@/app/utils/env";
import { isAdminDomain, isSubDomain } from "@/utils/core/generalUtils";
import {
  getCustomerId,
  getCustomerUserId,
  getWorkspace,
} from "@/utils/storage/appSession";
import { typedLocalStorage } from "@/utils/storage/localStorage";

interface ConfigState {
  expanded: boolean;
  hover: boolean;
  mobileExpand: boolean;
  isOpen: boolean;
  socketManagerRevision: number;
}

let socketManagerSingleton: Manager | null = null;

const createSocketManager = (): Manager => {
  const workspaceId = isSubDomain ? getWorkspace() : null;
  const customerId = !isSubDomain && !isAdminDomain ? getCustomerId() : null;
  const customerUserId =
    !isSubDomain && !isAdminDomain ? getCustomerUserId() : null;
  const isSecureSocketUrl = /^(https|wss):/i.test(env.SOCKET_URL ?? "");

  return new Manager(env.SOCKET_URL, {
    secure: isSecureSocketUrl,
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    timeout: 5000,
    requestTimeout: 5000,
    ...(!isAdminDomain && (workspaceId || customerId || customerUserId)
      ? {
          query: {
            ...(workspaceId && { workspaceId }),
            ...(customerId && { customerId }),
            ...(customerUserId && { customerUserId }),
          },
        }
      : {}),
  });
};

const closeSocketManager = (
  socketManager: Manager,
  isAutoRecovery = false,
): void => {
  if (isAutoRecovery) {
    try {
      const userData = typedLocalStorage.getItem(APP_LOCAL_KEYS.USER_DATA) as {
        id?: string;
        name?: string;
        firstName?: string;
        lastName?: string;
      } | null;
      const workspaceData = typedLocalStorage.getItem(
        APP_LOCAL_KEYS.WORKSPACE_DATA,
      );
      const activePage =
        typeof window !== "undefined" ? window.location.href : "unknown";

      const userName =
        userData?.name ||
        `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() ||
        "unknown";
      const userId = userData?.id || getCustomerUserId() || "unknown";

      Sentry.captureMessage("Forced socketManager to close", {
        level: "info",
        extra: {
          userName,
          userId,
          workspaceDetails: workspaceData || getWorkspace(),
          activePage,
          socketReadyState: socketManager.engine?.readyState || "unknown",
          transportName: socketManager.engine?.transport?.name || "unknown",
          reconnectAttemptsConfig: socketManager.reconnectionAttempts(),
          reconnectDelayConfig: socketManager.reconnectionDelay(),
        },
      });
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error("Failed to capture socket close log", error);
    }
  }

  socketManager.reconnection(false);
  const socketEngine = socketManager.engine;
  if (socketEngine) {
    if (socketEngine.readyState === "opening") {
      socketEngine.once?.("open", () => {
        socketEngine.close?.();
      });
    } else {
      socketEngine.close?.();
    }
  }
};

export const getSocketManager = (): Manager | null => {
  return socketManagerSingleton;
};

const initialState: ConfigState = {
  expanded: true,
  hover: false,
  mobileExpand: false,
  isOpen: false,
  socketManagerRevision: 0,
};

export const configSlice = createSlice({
  name: "Config",
  initialState,
  reducers: {
    toggleExpanded: (state) => {
      state.expanded = !state.expanded;
    },
    toggleMobileExpanded: (state) => {
      state.mobileExpand = !state.mobileExpand;
    },
    hoverExpanded: (state, action) => {
      state.hover = action.payload;
    },
    HideExportTable: (state) => {
      state.isOpen = !state.isOpen;
    },
    closeExpanded: (state) => {
      state.expanded = false;
    },
    initializeSocketManager: (
      state,
      action: PayloadAction<{ isAutoRecovery?: boolean } | undefined>,
    ) => {
      const existingSocketManager = socketManagerSingleton;

      if (existingSocketManager) {
        closeSocketManager(
          existingSocketManager,
          action.payload?.isAutoRecovery,
        );
      }

      socketManagerSingleton = createSocketManager();
      state.socketManagerRevision += 1;
    },
    destroySocketManager: (state) => {
      const existingSocketManager = socketManagerSingleton;

      if (existingSocketManager) {
        closeSocketManager(existingSocketManager);
        socketManagerSingleton = null;
        state.socketManagerRevision += 1;
      }
    },
  },
});

export const {
  toggleExpanded,
  hoverExpanded,
  toggleMobileExpanded,

  closeExpanded,
  initializeSocketManager,
  destroySocketManager,
} = configSlice.actions;

export default configSlice.reducer;
